import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Stethoscope, Video, Building2, Star, Search, ShieldCheck, Briefcase, Activity, Phone, ArrowRight, MapPin, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { doctorApi, consultationApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useLocationStore } from "@/store/locationStore";
import LocationPickerDialog from "@/components/LocationPickerDialog";
import SavePreferredButton from "@/components/SavePreferredButton";

const TIME_SLOTS = ["09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "02:00 PM", "04:00 PM", "06:00 PM"];

function initials(name) {
  if (!name) return "Dr";
  return name.replace(/^Dr\.?\s*/i, "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export default function DoctorListing() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.customerProfile);
  const locCity = useLocationStore((s) => s.city);
  const locLabel = useLocationStore((s) => s.label);
  const locCoords = useLocationStore((s) => s.coords);
  const detectLocation = useLocationStore((s) => s.detect);
  const locText = locLabel || locCity || "your area";
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const hospitalId = params.get("hospitalId");
  const hospitalName = params.get("hospitalName") || "";
  const initialMode = params.get("consultMode") === "video" ? "video" : "clinic";

  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState("");

  const [bookingDoc, setBookingDoc] = useState(null);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState(TIME_SLOTS[1]);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await doctorApi.search({
        hospital_id: hospitalId || undefined,
        video_only: mode === "video" ? true : undefined,
        search: search || undefined,
      });
      setDoctors(res.data?.doctors || res.data || []);
    } catch (e) {
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [hospitalId, mode, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => { if (!locCoords?.latitude) detectLocation(); }, [locCoords, detectLocation]);

  const openBooking = (doc) => {
    setBookingDoc(doc);
    setSlot(TIME_SLOTS[1]);
    setReason("");
    setDate("");
  };

  const submit = async () => {
    if (!date) return toast.error("Please choose a date");
    setSubmitting(true);
    try {
      const fee = mode === "video" ? bookingDoc.videoConsultationFee : bookingDoc.inPersonConsultationFee;
      await consultationApi.book({
        user_id: user._id,
        doctor_id: bookingDoc.id,
        doctor_name: bookingDoc.name,
        doctor_specialty: bookingDoc.specialty || "",
        hospital_id: hospitalId,
        hospital_name: hospitalName,
        patient_name: profile?.basicInfo?.fullName || user?.name || "",
        patient_phone: user?.phone || "",
        patient_age: String(profile?.basicInfo?.age || ""),
        patient_gender: profile?.basicInfo?.gender || "",
        reason: reason,
        preferred_date: date,
        preferred_time: slot,
        consult_mode: mode,
        fee: fee || 0,
        booking_type: "new",
      });
      toast.success("Appointment requested! Track it under For You.");
      setBookingDoc(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <button className="flex items-center gap-1 text-sm text-muted-foreground" onClick={() => navigate(-1)} data-testid="doctors-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Doctors</h1>
        {hospitalName && <p className="text-muted-foreground mt-1">at {hospitalName}</p>}
      </div>

      {/* Location bar — same UX as Book a Lab Test */}
      {!hospitalName && (
        <div className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <MapPin className="h-4 w-4 text-primary shrink-0" />
            <p className="text-sm min-w-0 truncate">
              <span className="text-muted-foreground">Showing doctors for </span>
              <span className="font-semibold">{locText}</span>
            </p>
          </div>
          <button className="text-sm font-semibold text-primary flex items-center gap-1 shrink-0" onClick={() => setShowLocationPicker(true)} data-testid="doctor-change-location">
            <Navigation className="h-3.5 w-3.5" /> Change
          </button>
        </div>
      )}

      {/* Mode toggle */}
      <div className="flex p-1 bg-muted rounded-2xl w-full max-w-xs">
        <button
          onClick={() => setMode("clinic")}
          data-testid="mode-clinic"
          className={`flex-1 h-10 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
            mode === "clinic" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"
          }`}
        >
          <Building2 className="h-4 w-4" /> In-Clinic
        </button>
        <button
          onClick={() => setMode("video")}
          data-testid="mode-video"
          className={`flex-1 h-10 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
            mode === "video" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"
          }`}
        >
          <Video className="h-4 w-4" /> Video Consult
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center rounded-2xl border border-input px-4 h-12 bg-card focus-within:ring-1 focus-within:ring-ring">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="doctor-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search doctor by name"
          className="border-0 focus-visible:ring-0 shadow-none"
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 rounded-3xl" />
          ))}
        </div>
      ) : doctors.length === 0 ? (
        <Card className="rounded-3xl p-8 text-center border-dashed">
          <Stethoscope className="h-10 w-10 text-muted-foreground mx-auto" />
          <p className="mt-3 font-medium">No doctors found</p>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === "video"
              ? "No doctors offer video consultation yet. Switch to In-Clinic."
              : "No doctors listed here yet."}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {doctors.map((doc) => {
            const fee = mode === "video" ? doc.videoConsultationFee : doc.inPersonConsultationFee;
            return (
              <Card key={doc.id} className="relative rounded-3xl p-5" data-testid={`doctor-card-${doc.id}`}>
                {doc.is_sanjeevani_verified !== false && (
                  <span className="absolute top-4 right-4 flex items-center gap-1 text-[11px] font-semibold text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: "#16a34a" }}>
                    <ShieldCheck className="h-3 w-3" /> Verified
                  </span>
                )}
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <Avatar className="h-16 w-16">
                      <AvatarFallback className="bg-secondary text-secondary-foreground text-lg">{initials(doc.name)}</AvatarFallback>
                    </Avatar>
                    {doc.isOnline && <span className="absolute bottom-0 right-0 h-4 w-4 rounded-full bg-green-500 border-2 border-white" />}
                  </div>
                  <div className="min-w-0 flex-1 pr-16">
                    <p className="font-semibold truncate">{doc.name}</p>
                    {doc.qualification && <p className="text-xs text-muted-foreground truncate">{doc.qualification}</p>}
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                      <span className="flex items-center gap-1" style={{ color: "#6366f1" }}><Briefcase className="h-3.5 w-3.5" /> {doc.experienceYears || 5}+ yrs</span>
                      <span className="flex items-center gap-1" style={{ color: "#f59e0b" }}><Star className="h-3.5 w-3.5" fill="#f59e0b" /> {Number(doc.rating || 4.5).toFixed(1)}</span>
                      {doc.surgeriesPerformed ? <span className="flex items-center gap-1" style={{ color: "#10b981" }}><Activity className="h-3.5 w-3.5" /> {doc.surgeriesPerformed}+ surgeries</span> : null}
                    </div>
                    <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                      <Building2 className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{doc.hospitalName || hospitalName || "Clinic"}</span>
                    </div>
                    <span className="inline-block mt-2 text-[11px] font-medium px-2.5 py-1 rounded-full bg-accent text-accent-foreground">{doc.specialty || "General Physician"}</span>
                  </div>
                </div>

                {/* Highlight bar */}
                <div className="flex items-stretch mt-4 rounded-2xl bg-muted/60 py-3">
                  <div className="flex-1 text-center">
                    <p className="font-semibold">{doc.successRate || 95}%</p>
                    <p className="text-[11px] text-muted-foreground">Success Rate</p>
                  </div>
                  <div className="w-px bg-border" />
                  <div className="flex-1 text-center">
                    <p className="font-semibold">{doc.totalReviews || 100}+</p>
                    <p className="text-[11px] text-muted-foreground">Happy Patients</p>
                  </div>
                  <div className="w-px bg-border" />
                  <div className="flex-1 text-center">
                    <p className="font-semibold" style={{ color: "#16a34a" }}>{fee ? `₹${fee}` : "At hospital"}</p>
                    <p className="text-[11px] text-muted-foreground">{mode === "video" ? "Video Consult" : "Consultation"}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 mt-4">
                  <SavePreferredButton userId={user?._id} provider={{ id: doc.id, name: doc.name, city: doc.city, address: doc.hospitalName || doc.address, logoUrl: doc.profileImage }} category="doctor" variant="icon" className="h-11 w-11 border border-border shrink-0" />
                  <Button variant="outline" className="flex-1 rounded-2xl h-11" onClick={() => toast.success("We'll call you back shortly!")} data-testid={`callback-${doc.id}`}>
                    <Phone className="h-4 w-4" style={{ color: "#6366f1" }} /> Call Me Back
                  </Button>
                  <Button className="flex-1 rounded-2xl h-11" onClick={() => openBooking(doc)} data-testid={`book-doctor-${doc.id}`}>
                    Book Consult <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!bookingDoc} onOpenChange={(o) => !o && setBookingDoc(null)}>
        <DialogContent className="rounded-3xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Book {bookingDoc?.name}</DialogTitle>
            <DialogDescription>
              {mode === "video" ? "Video consultation" : "In-clinic visit"} • Pay at the hospital
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ddate">Date</Label>
              <Input
                id="ddate"
                type="date"
                value={date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1.5 rounded-2xl h-11"
                data-testid="consult-date"
              />
            </div>
            <div>
              <Label>Time slot</Label>
              <Select value={slot} onValueChange={setSlot}>
                <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="consult-slot">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Describe your concern briefly"
                className="mt-1.5 rounded-2xl"
                data-testid="consult-reason"
              />
            </div>
          </div>
          <Button className="w-full rounded-2xl h-11 mt-2" onClick={submit} disabled={submitting} data-testid="confirm-consult-booking">
            {submitting ? "Requesting..." : "Request Appointment"}
          </Button>
        </DialogContent>
      </Dialog>

      <LocationPickerDialog open={showLocationPicker} onOpenChange={setShowLocationPicker} />
    </div>
  );
}
